// Color helpers for user-chosen hexes (stages, territories, teams, users, visit results). The iOS app stores colors as
// "#RRGGBB" strings and renders them with Theme.color(hex:) (GroundTeam/Support/Theme.swift), falling back to gray
// for anything it cannot parse; these helpers keep the same rules so a color looks the same on both sides, and add
// the contrast checks a browser needs (SwiftUI adjusts text for us; CSS does not).

import type { CSSProperties } from 'react';

/** Brand colors (Theme.swift / WEB_PLAN.md §1.1), for code that needs a hex rather than a CSS variable. */
export const GT_COLORS = {
  navy: '#102A5E',
  navyDeep: '#0A1C42',
  orange: '#F96A3C',
  coral: '#FF8C6B',
  blue: '#2673E6',
  purple: '#7D4DBF',
  red: '#DB4038',
  amber: '#F5B400',
  green: '#29A85C',
  grey: '#8C919E',
  teal: '#009E9E',
} as const;

export type GtColorName = keyof typeof GT_COLORS;

/** The color picker palette (Theme.palette), in the iOS order. */
export const GT_PALETTE: readonly { name: string; hex: string }[] = [
  { name: 'Orange', hex: '#F96A3C' },
  { name: 'Amber', hex: '#F5B400' },
  { name: 'Green', hex: '#29A85C' },
  { name: 'Teal', hex: '#009E9E' },
  { name: 'Blue', hex: '#2673E6' },
  { name: 'Navy', hex: '#102A5E' },
  { name: 'Purple', hex: '#7D4DBF' },
  { name: 'Pink', hex: '#D9469C' },
  { name: 'Red', hex: '#DB4038' },
  { name: 'Gray', hex: '#8C919E' },
  { name: 'Brown', hex: '#8B5E3C' },
  { name: 'Lime', hex: '#8BC34A' },
];

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/**
 * Parses "#RRGGBB" or "RRGGBB" (surrounding whitespace ignored), exactly the inputs Theme.color(hex:) accepts.
 * Returns null for anything else (3-digit shorthand, names, alpha), which callers render as gray like iOS.
 */
export function parseHex(hex: string | null | undefined): Rgb | null {
  if (typeof hex !== 'string') return null;
  let s = hex.trim();
  if (s.startsWith('#')) s = s.slice(1);
  if (!/^[0-9a-fA-F]{6}$/.test(s)) return null;
  const v = Number.parseInt(s, 16);
  return { r: (v >> 16) & 0xff, g: (v >> 8) & 0xff, b: v & 0xff };
}

function toHex({ r, g, b }: Rgb): string {
  const part = (n: number) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`.toUpperCase();
}

/** "#RRGGBB" upper-case, or the fallback (gray, like iOS) when the value does not parse. */
export function normalizeHex(hex: string | null | undefined, fallback: string = GT_COLORS.grey): string {
  const rgb = parseHex(hex);
  return rgb ? toHex(rgb) : fallback;
}

/** `rgb(r g b / a)` for a hex; unparseable input uses gray. */
export function withAlpha(hex: string | null | undefined, alpha: number): string {
  const { r, g, b } = parseHex(hex) ?? (parseHex(GT_COLORS.grey) as Rgb);
  return `rgb(${r} ${g} ${b} / ${Math.max(0, Math.min(1, alpha))})`;
}

/** Linear blend: t = 0 gives `a`, t = 1 gives `b`. */
export function mixHex(a: string, b: string, t: number): string {
  const x = parseHex(a) ?? (parseHex(GT_COLORS.grey) as Rgb);
  const y = parseHex(b) ?? (parseHex(GT_COLORS.grey) as Rgb);
  const k = Math.max(0, Math.min(1, t));
  return toHex({ r: x.r + (y.r - x.r) * k, g: x.g + (y.g - x.g) * k, b: x.b + (y.b - x.b) * k });
}

/** WCAG relative luminance (0 black … 1 white). */
export function relativeLuminance(hex: string): number {
  const rgb = parseHex(hex) ?? (parseHex(GT_COLORS.grey) as Rgb);
  const channel = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/** WCAG contrast ratio between two colors (1 … 21). */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [hi, lo] = la >= lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
}

/**
 * Text color for content drawn ON a solid fill of `hex` (filled chips, avatars, priority badges): white unless navy
 * reads better. iOS always uses white; that fails on light fills such as amber (#F5B400 is ~1.9:1), so the web picks
 * the more legible of the two brand text colors.
 */
export function readableTextOn(hex: string | null | undefined): string {
  const fill = normalizeHex(hex);
  const white = contrastRatio(fill, '#FFFFFF');
  const navy = contrastRatio(fill, GT_COLORS.navy);
  // Prefer white (the iOS look) whenever it is at least as legible as navy or clears the large-text bar.
  return white >= navy || white >= 3.2 ? '#FFFFFF' : GT_COLORS.navy;
}

/**
 * The closest shade of `hex` that reaches `min` contrast against `background`: darkened toward black on light
 * backgrounds, lightened toward white on dark ones. Used for tinted chips, whose text is the chip's own color (iOS
 * Chip) and would otherwise vanish for yellows on white or navies on dark.
 */
export function readableShade(hex: string | null | undefined, background: string, min = 4.5): string {
  const base = normalizeHex(hex);
  if (contrastRatio(base, background) >= min) return base;
  const target = relativeLuminance(background) > 0.35 ? '#000000' : '#FFFFFF';
  for (let t = 0.05; t <= 1.0001; t += 0.05) {
    const shade = mixHex(base, target, t);
    if (contrastRatio(shade, background) >= min) return shade;
  }
  return target;
}

/** Card surfaces from tokens.css, used as the backgrounds tinted chips sit on. */
const SURFACE_LIGHT = '#FFFFFF';
const SURFACE_DARK = '#161B26';
const CHIP_ALPHA_LIGHT = 0.13;
const CHIP_ALPHA_DARK = 0.22;

/**
 * Inline style for a chip/badge in a user-chosen color, in both themes at once via CSS `light-dark()` (tokens.css sets
 * `color-scheme` from the chosen theme, so it follows System / Light / Dark):
 * - tinted (iOS Chip default): the color at 13% (22% in dark) behind text in a readable shade of the same color;
 * - filled (iOS `filled: true`): a solid fill with white or navy text, whichever reads.
 */
export function tintStyle(hex: string | null | undefined, filled = false): CSSProperties {
  const base = normalizeHex(hex);
  if (filled) {
    return {
      backgroundColor: base,
      backgroundImage: `linear-gradient(180deg, ${mixHex(base, '#FFFFFF', 0.12)}, ${base})`,
      color: readableTextOn(base),
    };
  }
  const bgLight = mixHex(SURFACE_LIGHT, base, CHIP_ALPHA_LIGHT);
  const bgDark = mixHex(SURFACE_DARK, base, CHIP_ALPHA_DARK);
  return {
    backgroundColor: `light-dark(${withAlpha(base, CHIP_ALPHA_LIGHT)}, ${withAlpha(base, CHIP_ALPHA_DARK)})`,
    color: `light-dark(${readableShade(base, bgLight)}, ${readableShade(base, bgDark)})`,
  };
}

/** A readable foreground for an icon or text in `hex` on the card surface, in both themes (IconBadge glyphs). */
export function readableForeground(hex: string | null | undefined): string {
  const base = normalizeHex(hex);
  return `light-dark(${readableShade(base, mixHex(SURFACE_LIGHT, base, CHIP_ALPHA_LIGHT), 3)}, ${readableShade(
    base,
    mixHex(SURFACE_DARK, base, CHIP_ALPHA_DARK),
    3,
  )})`;
}

/**
 * Initials like InitialsAvatar: the first letter of the first two words, upper-cased; "?" when there are none.
 * Uses code points so an emoji or accented first letter is not split in half.
 */
export function initialsOf(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean).slice(0, 2);
  const letters = parts.map((p) => (Array.from(p)[0] ?? '').toLocaleUpperCase()).join('');
  return letters || '?';
}
